import React from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import Chart from 'react-apexcharts'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'

import { usePlace } from 'commons/usePlace'

import { channelsType } from '../constants'

const useStyles = makeStyles((theme) => ({
  color: {
    color: '#404040',
  },
}))

export function BarChart({ accumulate, beacon }) {
  const place = usePlace()
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const isLoadingBeacon = R.isNil(beacon)
  const disableAutomatedCall = isLoadingBeacon
    ? true
    : !place.enabledAutomatedCall
  const disableBeacon = isLoadingBeacon ? true : beacon === 0

  const accumulateObject =
    !R.isEmpty(accumulate) &&
    accumulate.data.reduce((obj, item) => {
      obj[item.type] = item
      return obj
    }, [])

  const channels = [
    {
      label: formatMessage({ id: 'channel.messageSringApp' }).split(' '),
      data: accumulateObject[channelsType.ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(3, 114, 177, 0.30)',
      labelColor: '#404040',
    },
    {
      label: formatMessage({ id: 'channel.sms' }).split(' '),
      data: accumulateObject[channelsType.SMS_ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(242, 174, 20, 0.30)',
      labelColor: '#404040',
    },
    {
      label: formatMessage({ id: 'channel.email' }).split(' '),
      data: accumulateObject[channelsType.EMAIL_ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(247, 72, 114, 0.30)',
      labelColor: '#404040',
    },
    {
      label: formatMessage({ id: 'channel.socialMedia' }).split(' '),
      data: accumulateObject[channelsType.SOCIAL_ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(73, 130, 249, 0.30)',
      labelColor: '#404040',
    },
    {
      label: formatMessage({ id: 'channel.automatedCall' }).split(' '),
      data: accumulateObject[channelsType.AUTOMATED_CALL_ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(92, 203, 123, 0.30)',
      labelColor: disableAutomatedCall ? '#C4C4C4' : '#404040',
    },
    {
      label: formatMessage({ id: 'channel.beacon' }).split(' '),
      data: accumulateObject[channelsType.BEACON_ALERT]?.sent ?? 0,
      backgroundColor: 'rgba(123, 97, 255, 0.30)',
      labelColor: disableBeacon ? '#C4C4C4' : '#404040',
    },
  ]

  const chart = {
    series: [
      {
        data: channels.map((channel) => channel?.data),
      },
    ],
    options: {
      chart: {
        type: 'bar-chart-usage-summary',
        height: 350,
        zoom: {
          autoScaleYaxis: false,
        },
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
          distributed: true,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return Number(val ?? 0).toLocaleString('en-US')
        },
        // offsetY: -25,
        style: {
          fontSize: '14px',
          colors: channels.map((channel) => channel?.labelColor),
        },
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories: channels.map((channel) => channel.label),
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: true,
          color: '#DADADA',
          height: 1,
          width: '100%',
        },
        tooltip: {
          enabled: false,
        },
        labels: {
          style: {
            colors: channels.map((channel) => channel?.labelColor),
          },
        },
      },
      yaxis: {
        axisBorder: {
          show: true,
          color: '#DADADA',
        },
        labels: {
          show: true,
          style: {
            colors: '#404040',
            fontFamily: 'Roboto',
          },
          formatter: (value) => {
            return parseFloat(value).toFixed(0).toLocaleString('en-US')
          },
        },
        min: (min) => {
          return min
        },
        max: (max) => {
          return max + 100
        },
        tickAmount: 6,
      },
      noData: {
        text: 'Loading....',
        style: {
          fontSize: '16px',
          colors: '#000000',
          fontFamily: 'Roboto',
        },
      },
      tooltip: {
        enabled: false,
      },
      colors: channels.map((channel) => channel.backgroundColor),
    },
  }

  return (
    <>
      <div className="bg-white pb-20 px-10 rounded-8 border-1">
        <div
          className={clsx(
            'text-16 font-bold flex items-center my-10',
            classes.color,
          )}
        >
          <FormattedMessage id="text.usage.channels" />
        </div>
        <Chart
          options={chart.options}
          series={chart.series}
          type="bar"
          height={350}
        />{' '}
      </div>
    </>
  )
}
